import type { IntegrationResponseDto } from '../../../openapi/models/IntegrationResponseDto'

/**
 * The destination a connected LinkedIn row publishes to, stored on the single
 * `linkedin` integration's `config.linkedinTarget`. `null`/absent means the
 * user authorized the Company-Page app but administers multiple Pages, so the
 * pick is still pending — treated as a first-class "action needed" state.
 */
type LinkedInTargetConfig = {
  type: 'person' | 'organization'
  id: string
  name?: string
  avatarUrl?: string
}

function readLinkedInTarget(integration: IntegrationResponseDto): LinkedInTargetConfig | null {
  const target = integration.config?.linkedinTarget as LinkedInTargetConfig | null | undefined
  return target ?? null
}

/**
 * A connected `linkedin` integration with no destination chosen yet
 * (`config.linkedinTarget` null/absent) means the user authorized the
 * Company-Page app but administers multiple Pages, so the pick is still
 * pending. Mirrors Facebook's `pageId`-less "needs a Page" affordance.
 * Personal connects auto-bind their single person target at OAuth time and
 * never reach this state.
 */
export function linkedinNeedsTarget(integration: IntegrationResponseDto): boolean {
  return (
    integration.provider === 'linkedin' &&
    integration.isConnected &&
    !integration.needsReconnect &&
    readLinkedInTarget(integration) === null
  )
}

/**
 * Display name + avatar for the connected LinkedIn summary row (Company Page
 * name for `organization`, "Personal profile" for `person`). Only meaningful
 * when `linkedinNeedsTarget` is false — a pending pick shows the "action
 * needed" CTA instead.
 */
export function resolveLinkedInTargetSummary(integration: IntegrationResponseDto): {
  type: 'person' | 'organization'
  name: string
  avatarUrl?: string
} {
  const target = readLinkedInTarget(integration)
  if (target?.type === 'person') {
    return {
      type: 'person',
      name: target.name || 'Personal profile',
      avatarUrl: target.avatarUrl,
    }
  }
  return {
    type: 'organization',
    name: target?.name || 'Company Page',
    avatarUrl: target?.avatarUrl,
  }
}
